import useCreditCardForm from '~/shared/CreditCardForm/hooks/useCreditCardForm';
import { Caption } from '~/shared/Typography';
import { Box, FormControl, Input, WarningIcon, VStack, HStack } from 'native-base';
import Button from '~/shared/Button';
import { useFormStyles, useGeneralStyles } from '~/shared/styles';
import { SpIcon } from '~/shared/Icon';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import Pressable from '~/shared/Pressable';
import { CreditCardDto } from '~/shared/CreditCardForm/hooks/useCreditCardForm';
import React, { ReactNode } from 'react';
import { i18nFn } from '@springbig/shared';

type CreditCardFormProps = {
    submitButtonText?: string;
    summary?: string;
    error?: string[] | string;
    onCancel: () => void;
    onProceed: (data: CreditCardDto) => void;
    isLoading?: boolean;
    showCancel?: boolean;
    titleComponent?: ReactNode;
    t: i18nFn
};

const CreditCardForm = ({
    submitButtonText,
    summary,
    error,
    onCancel,
    onProceed,
    isLoading,
    showCancel = true,
    titleComponent,
    t,
}: CreditCardFormProps) => {

    const {
        input_background,
        input_border,
        input_label,
        input_text
    } = useFormStyles();
    const gStyle = useGeneralStyles();

    const {
        isAmExCard,
        isNumberVisible,
        isNumberFieldFocused,
        numberFieldRef,
        formMethods,
        nameField,
        numberField,
        cvvField,
        expiryField,
        zipField,
        onCardNumberChange,
        onExpiryChange,
        setBooleanState,
        onSubmit,
    } = useCreditCardForm({
        onProceed,
    });

    return (
        <FormControl isInvalid={!!error}>
            { titleComponent && titleComponent }
            <VStack space={4}>
                <FormControl isInvalid={!!formMethods.errors.name}>
                    <FormControl.Label color={input_label}>
                        { t('credit_card.name') }
                    </FormControl.Label>
                    <Input
                        {...nameField}
                        backgroundColor={input_background}
                        borderColor={input_border}
                        height={10}
                        fontSize={16}
                        color={input_text}
                        isInvalid={!!formMethods.errors.name}
                        isDisabled={isLoading}
                        maxLength={255}
                        returnKeyType='done'
                    />
                    <FormControl.ErrorMessage
                        leftIcon={<WarningIcon size={3} />}
                    >
                        { formMethods.errors.name?.message }
                    </FormControl.ErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!formMethods.errors.number}>
                    <FormControl.Label color={input_label}>
                        { t('credit_card.number') }
                    </FormControl.Label>
                    { isNumberVisible ?
                        <Input
                            {...numberField}
                            keyboardType={'numberPad'}
                            backgroundColor={input_background}
                            borderColor={input_border}
                            height={10}
                            fontSize={16}
                            color={input_text}
                            isInvalid={!!formMethods.errors.number}
                            isDisabled={isLoading}
                            maxLength={19}
                            returnKeyType='done'
                            onChangeText={(value) => {

                                onCardNumberChange(value);
                            }}
                            InputRightElement={
                                <Pressable
                                    onPress={() => setBooleanState('isNumberVisible', !isNumberVisible)}
                                    px={2}
                                >
                                    <SpIcon
                                        icon={isNumberVisible ? faEyeSlash : faEye}
                                        color={'grey'}
                                    />
                                </Pressable>
                            }
                        />
                        :
                        <Box>
                            <Input
                                {...numberField}
                                ref={numberFieldRef}
                                keyboardType={'numberPad'}
                                backgroundColor={input_background}
                                borderColor={input_border}
                                height={10}
                                fontSize={16}
                                color={input_text}
                                isInvalid={!!formMethods.errors.number}
                                isDisabled={isLoading}
                                maxLength={19}
                                returnKeyType='done'
                                onChangeText={(value) => {

                                    onCardNumberChange(value);
                                }}
                                InputRightElement={
                                    <Pressable
                                        onPress={() => setBooleanState('isNumberVisible', !isNumberVisible)}
                                        px={2}
                                    >
                                        <SpIcon
                                            icon={isNumberVisible ? faEyeSlash : faEye}
                                            color={'grey'}
                                        />
                                    </Pressable>
                                }
                            />
                            <Pressable
                                position={'absolute'}
                                backgroundColor={input_background}
                                top={1}
                                left={1}
                                right={10}
                                bottom={1}
                                justifyContent={'center'}
                                px={2}
                                onPress={() => {

                                    numberFieldRef.current.focus();
                                    setBooleanState('isNumberFieldFocused', true);
                                }}
                                onBlur={() => setBooleanState('isNumberFieldFocused', false)}
                            >
                                <Caption
                                    fontSize={18}
                                    opacity={.5}
                                >
                                    {'●'.repeat(numberField.value.length)}{isNumberFieldFocused ? '|' : ''}
                                </Caption>
                            </Pressable>
                        </Box>
                    }
                    <FormControl.ErrorMessage
                        leftIcon={<WarningIcon size={3} />}
                    >
                        { formMethods.errors.number?.message }
                    </FormControl.ErrorMessage>
                </FormControl>

                <HStack space={1}>
                    <FormControl flex={1} isInvalid={!!formMethods.errors.cvv}>
                        <FormControl.Label color={input_label}>
                            { t('credit_card.cvv') }
                        </FormControl.Label>
                        <Input
                            {...cvvField}
                            keyboardType={'numberPad'}
                            backgroundColor={input_background}
                            borderColor={input_border}
                            height={10}
                            fontSize={16}
                            color={input_text}
                            isInvalid={!!formMethods.errors.cvv}
                            isDisabled={isLoading}
                            maxLength={isAmExCard ? 4 : 3}
                            returnKeyType='done'
                        />
                        <FormControl.ErrorMessage
                            leftIcon={<WarningIcon size={3} />}
                        >
                            { formMethods.errors.cvv?.message }
                        </FormControl.ErrorMessage>
                    </FormControl>

                    <FormControl flex={1} isInvalid={!!formMethods.errors.expiry}>
                        <FormControl.Label color={input_label}>
                            { t('credit_card.expiry') }
                        </FormControl.Label>
                        <Input
                            {...expiryField}
                            keyboardType={'numberPad'}
                            backgroundColor={input_background}
                            borderColor={input_border}
                            height={10}
                            fontSize={16}
                            color={input_text}
                            placeholder={'mm/yy'}
                            isInvalid={!!formMethods.errors.expiry}
                            isDisabled={isLoading}
                            onChangeText={onExpiryChange}
                            maxLength={5}
                            returnKeyType='done'
                        />
                        <FormControl.ErrorMessage
                            leftIcon={<WarningIcon size={3} />}
                        >
                            { formMethods.errors.expiry?.message }
                        </FormControl.ErrorMessage>
                    </FormControl>

                    <FormControl flex={1} isInvalid={!!formMethods.errors.zip}>
                        <FormControl.Label color={input_label}>
                            { t('credit_card.zip_code') }
                        </FormControl.Label>
                        <Input
                            {...zipField}
                            backgroundColor={input_background}
                            borderColor={input_border}
                            height={10}
                            fontSize={16}
                            color={input_text}
                            isInvalid={!!formMethods.errors.zip}
                            isDisabled={isLoading}
                            maxLength={7}
                            returnKeyType='done'
                        />
                        <FormControl.ErrorMessage
                            leftIcon={<WarningIcon size={3} />}
                        >
                            { formMethods.errors.zip?.message }
                        </FormControl.ErrorMessage>
                    </FormControl>
                </HStack>

                <FormControl.ErrorMessage>
                    <WarningIcon size={3} pr={2} color={'inherit'} />
                    <Caption>
                        { error && error[0] }
                    </Caption>
                </FormControl.ErrorMessage>

                <Caption textAlign={'center'}>
                    { summary }
                </Caption>

                <Button
                    bg={gStyle.button_background}
                    _text={{ color: gStyle.button_text }}
                    onPress={formMethods.handleSubmit(onSubmit)}
                    isLoading={isLoading}
                >
                    { submitButtonText || t('general.submit') }
                </Button>

                { showCancel &&
                    <Button
                        bg={gStyle.button_neutral_background}
                        _text={{ color: gStyle.button_neutral_text }}
                        onPress={onCancel}
                        isLoading={isLoading}
                    >
                        { t('credit_card.cancel_button') }
                    </Button>
                }
                <Caption textAlign='center' py={2}>{t('credit_card.disclaimer')}</Caption>
            </VStack>
        </FormControl>
    );
};

export default CreditCardForm;
